import React from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useFieldControl } from './Field';

/**
 * QtyStepper, the quantity control.
 *
 * Source selectors: `system.css` `.dsk-qty`, `.dsk-qty__value`,
 * `.dsk-qty__input`, and the bare `button` rules inside `.dsk-qty`.
 *
 * Doctrine enforced:
 * - ONE bordered control, not three loose buttons, so it reads as a single
 *   value being adjusted rather than as a toolbar.
 * - At the ends the buttons are `disabled` for real, never dimmed with opacity,
 *   and the CSS gives disabled its flat grey.
 * - The focus ring here is an INSET `outline`, not the usual box-shadow ring,
 *   because the wrapper is `overflow: hidden` and would clip a shadow drawn
 *   outside the button box. That is a CSS decision; nothing here fights it.
 * - Icon-only buttons, so both carry an `aria-label`, and the read-only value
 *   is a live region so a screen reader hears the new count after a press.
 *
 * `editable` swaps `.dsk-qty__value` for `.dsk-qty__input`, the real number
 * input, for the cart where typing 12 beats pressing plus eleven times.
 */
export const QtyStepper = React.forwardRef(function QtyStepper(
  {
    value,
    defaultValue = 1,
    onChange,
    min = 1,
    max,
    step = 1,
    editable = false,
    label = 'Quantity',
    decreaseLabel = 'Decrease quantity',
    increaseLabel = 'Increase quantity',
    disabled = false,
    id,
    className,
    ...rest
  },
  ref,
) {
  const field = useFieldControl({ id });
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState(defaultValue);
  const current = isControlled ? value : internal;

  const clamp = React.useCallback(
    (next) => {
      let out = next;
      if (min !== undefined && out < min) out = min;
      if (max !== undefined && out > max) out = max;
      return out;
    },
    [min, max],
  );

  const set = React.useCallback(
    (next) => {
      const clamped = clamp(next);
      if (!isControlled) setInternal(clamped);
      if (onChange) onChange(clamped);
    },
    [clamp, isControlled, onChange],
  );

  const atMin = min !== undefined && current <= min;
  const atMax = max !== undefined && current >= max;

  return (
    <div ref={ref} className={cn('dsk-qty', className)} {...rest}>
      <button
        type="button"
        aria-label={decreaseLabel}
        disabled={disabled || atMin}
        onClick={() => set(current - step)}
      >
        <Icon as={Minus} size="sm" />
      </button>

      {editable ? (
        <input
          className="dsk-qty__input"
          type="number"
          inputMode="numeric"
          aria-label={label}
          value={current}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          id={field.id}
          onChange={(event) => {
            const next = Number.parseInt(event.target.value, 10);
            if (!Number.isNaN(next)) set(next);
          }}
        />
      ) : (
        <span className="dsk-qty__value" aria-live="polite">
          {current}
        </span>
      )}

      <button
        type="button"
        aria-label={increaseLabel}
        disabled={disabled || atMax}
        onClick={() => set(current + step)}
      >
        <Icon as={Plus} size="sm" />
      </button>
    </div>
  );
});
QtyStepper.displayName = 'QtyStepper';

export default QtyStepper;
